import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './RequestsDetails.css';
import axiosInstance from '../../../utils/axiosInstance';
import { IoIosArrowBack } from 'react-icons/io';
import { toast } from 'react-toastify';
import { saveAs } from 'file-saver';
import { Form, Button, Col, Row, Modal, Spinner } from 'react-bootstrap';
import PasswordInput from './PasswordInput';
import { useTranslation } from 'react-i18next';

const RequestDetails = () => {
    const { t } = useTranslation();
    const { state } = useLocation();
    const { request, loggedInEmployeeId, empRole: loggedInEmpRole } = state || {};
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [showPrompt, setShowPrompt] = useState(false);
    const [showDeclinePrompt, setShowDeclinePrompt] = useState(false);
    const [declineComment, setDeclineComment] = useState('');
    const [approvedEmployeeId, setApprovedEmployeeId] = useState(null);
    const [editableRequest, setEditableRequest] = useState(request || {});
    const [step, setStep] = useState(1);
    const [errors, setErrors] = useState({});

    const [companies, setCompanies] = useState([]);
    const [branches, setBranches] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [managerInfo, setManagerInfo] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState('');
    const [selectedBranch, setSelectedBranch] = useState('');
    const [selectedDept, setSelectedDept] = useState('');
    const [selectedManager, setSelectedManager] = useState('');
    const [passwordMatch, setPasswordMatch] = useState(null);
    const [passwordStrength, setPasswordStrength] = useState('');

    const [formData, setFormData] = useState({});
    const [employeeSapId, setEmployeeSapId] = useState('');
    const [previewUrl, setPreviewUrl] = useState(null);
    const [mimeType, setMimeType] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false); // State to control modal visibility
    const [currentFileType, setCurrentFileType] = useState('');
    const [loading, setLoading] = useState(false);
    const [empRole, setEmpRole] = useState('User'); // Default to 'User'

    const nextStep = () => {
        if (validateForm()) {
            setStep(step);
        }
    };

    useEffect(() => {
        if (loggedInEmpRole !== 'SuperAdmin') return;
        if (request && request.id) {
            const updateStatus = async () => {
                try {
                    await axiosInstance.post('/admin/update-status', {
                        requestId: request.id,
                        status: 'In-Review'
                    });
                } catch (error) {
                    console.error('Error updating status:', error);
                    toast.error(t('Error updating status. Please try again.'));
                }
            };
            updateStatus();
        }
    }, [request, loggedInEmpRole]);

    useEffect(() => {
        if (request) {
            setEditableRequest(request);
            setSelectedCompany(request.company_id || '');
            setSelectedBranch(request.branch_id || '');
            setSelectedDept(request.dept_id || '');
            setSelectedManager(request.admin_id || '');
        }
    }, [request]);

    useEffect(() => {
        // Fetch companies on component mount
        const response = axiosInstance.get('/user/companies')
            .then(response => setCompanies(response.data))
            .catch(error => console.error('Error fetching companies:', error));
    }, []);

    useEffect(() => {
        if (selectedCompany) {
            // Fetch branches when a company is selected
            axiosInstance.get(`/user/branches/${selectedCompany}`)
                .then(response => setBranches(response.data))
                .catch(error => console.error('Error fetching branches:', error));

            // Reset branch, department, and manager selections
            setSelectedBranch('');
            setSelectedDept('');
            setSelectedManager('');
        } else {
            setBranches([]);
        }
    }, [selectedCompany]);

    useEffect(() => {
        if (selectedBranch) {
            // Fetch departments when a branch is selected
            axiosInstance.get(`/user/departments/${selectedBranch}`)
                .then(response => setDepartments(response.data))
                .catch(error => console.error('Error fetching departments:', error));

            // Reset department and manager selections
            setSelectedDept('');
            setSelectedManager('');
        } else {
            setDepartments([]);
        }
    }, [selectedBranch]);

    useEffect(() => {
        if (selectedCompany && selectedDept) {
            // Fetch Department Manager info when a company and department are selected
            axiosInstance.get(`/user/dept_manager-info/${selectedCompany}`)
                .then(response => setManagerInfo(response.data))
                .catch(error => {
                    console.error('Error fetching manager info:', error);
                    if (error.response && error.response.status === 404) {
                        // If the endpoint is not found, set an empty array for manager info
                        setManagerInfo([]);
                        console.log(t('Manager info endpoint not found. Setting empty array.'));
                    } else {
                        // For other errors, you might want to show a toast or some other user feedback
                        toast.error(t('Failed to fetch manager information. Please try again later.'));
                    }
                });
        } else {
            setManagerInfo([]);
        }
    }, [selectedCompany, selectedDept]);

    // Handle password changes
    const handlePasswordChange = (password, confirmPassword, strength, match) => {
        setEditableRequest((prevData) => ({
            ...prevData,
            password: password,
            confirmPassword: confirmPassword,
        }));
        setPasswordStrength(strength);
        setPasswordMatch(match);
    };

    // Update `handleChange` to handle company, branch, and department fields
    const handleCompanyChange = (e) => {
        const value = e.target.value;
        setSelectedCompany(value);
        setEditableRequest((prevData) => ({
            ...prevData,
            company_id: value,
        }));
    };

    const handleBranchChange = (e) => {
        const value = e.target.value;
        setSelectedBranch(value);
        setEditableRequest((prevData) => ({
            ...prevData,
            branch_id: value,
        }));
    };

    const handleDeptChange = (e) => {
        const value = e.target.value;
        setSelectedDept(value);
        setEditableRequest((prevData) => ({
            ...prevData,
            dept_id: value,
        }));
    };

    const handleManagerChange = (e) => {
        const value = e.target.value;
        setSelectedManager(value);
        setEditableRequest((prevData) => ({
            ...prevData,
            admin_id: value,
        }));
    };

    const handleBackClick = () => {
        navigate(-1);
    };

    const handleApproveClick = () => {
        if (loggedInEmpRole !== 'SuperAdmin') {
            toast.error(t('Only SuperAdmin can approve requests.'));
            return;
        }
        setShowPrompt(true);
    };

    const handleDeclineClick = () => {
        if (loggedInEmpRole !== 'SuperAdmin') {
            toast.error(t('Only SuperAdmin can decline requests.'));
            return;
        }
        setShowDeclinePrompt(true);
    };

    const formatDateForMySQL = (dateString) => {
        const date = new Date(dateString);
        return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    };

    const handleApproveConfirm = async () => {
        if (validateForm(true)) {  // Pass true to indicate form is being submitted
            try {
                setIsSubmitting(true); // Disable submit button

                const formattedDate = formatDateForMySQL(editableRequest.admission_date);

                const employeeInfoResponse = await axiosInstance.post('/admin/insert-employee-info', {
                    empSapId: editableRequest.emp_sap_id,
                    hfEmail: editableRequest.hf_email,
                    password: editableRequest.password,
                    profession: editableRequest.profession,
                    admissionDate: formattedDate,
                    loggedInEmployeeId: loggedInEmployeeId,
                    companyId: editableRequest.company_id,
                    branchId: editableRequest.branch_id,
                    deptId: editableRequest.dept_id,
                    empRole: empRole // Use empRole here
                });

                const employeeId = employeeInfoResponse.data.empId;

                await axiosInstance.post('/admin/insert-personal-info', {
                    empSapId: editableRequest.emp_sap_id,
                    empId: employeeId,
                    fullName: editableRequest.full_name,
                    hfEmail: editableRequest.hf_email,
                    hfCellPhone: editableRequest.hf_cell_phone,
                    address: editableRequest.address
                });

                await axiosInstance.post('/admin/approve-request-empId', {
                    empSapId: editableRequest.emp_sap_id,
                    requestId: editableRequest.id,
                    empId: employeeId,
                    fullName: editableRequest.full_name,
                    admissionDate: formattedDate,
                    costCenter: editableRequest.cost_center,
                    dateOfBirth: formatDateForMySQL(editableRequest.date_of_birth),
                    placeOfBirth: editableRequest.place_of_birth,
                    nationality: editableRequest.nationality,
                    gender: editableRequest.gender,
                    maritalStatus: editableRequest.marital_status,
                    address: editableRequest.address,
                    postalCode: editableRequest.postal_code,
                    locality: editableRequest.locality,
                    privateMobile: editableRequest.private_mobile,
                    hfCellPhone: editableRequest.hf_cell_phone,
                    privateEmail: editableRequest.private_email,
                    hfEmail: editableRequest.hf_email,
                    emergencyContactMobile: editableRequest.emergency_contact_mobile,
                    emergencyContactType: editableRequest.emergency_contact_type,
                    profession: editableRequest.profession,
                    hfFunction: editableRequest.hf_function,
                    academicQualifications: editableRequest.academic_qualifications,
                    remarks: editableRequest.remarks,
                    taxpayerNumber: editableRequest.taxpayer_number,
                    socialSecurityNumber: editableRequest.social_security_number,
                    beneficiaryNumber: editableRequest.beneficiary_number,
                    iban: editableRequest.iban,
                    swift: editableRequest.swift,
                    citizenCardOrPassport: editableRequest.citizen_card_no_passport,
                    expiryCcOrPassport: formatDateForMySQL(editableRequest.expiry_date_cc_passport),
                    drivingLicenseNo: editableRequest.driving_license_no,
                    categories: editableRequest.categories,
                    drivingLicenseIssue: formatDateForMySQL(editableRequest.driving_license_issue_date),
                    drivingLicenseValidity: formatDateForMySQL(editableRequest.driving_license_validity_date),
                    noOfDependents: editableRequest.no_of_dependents,
                    adminEmailId: editableRequest.admin_id,
                    password: editableRequest.password,
                    companyId: editableRequest.company_id,
                    branchId: editableRequest.branch_id,
                    deptId: editableRequest.dept_id
                });

                setApprovedEmployeeId(employeeId);
                setShowPrompt(false);
                setIsSubmitting(false); // Disable submit button
                navigate('/registrationRequests', { state: { refresh: Date.now() } });
                toast.success(t("Employee Successfully Added."));
            } catch (error) {
                console.error('Error approving request:', error);
                if (error?.response?.status === 409) {
                    toast.info(t('This request is already approved.'));
                    setShowPrompt(false);
                    navigate('/registrationRequests', { state: { refresh: Date.now() } });
                } else if (error?.response?.status === 403) {
                    toast.error(t('Only SuperAdmin can approve requests.'));
                    setShowPrompt(false);
                } else {
                    toast.error(t('Error approving request. Please try again.'));
                }
                setIsSubmitting(false);
            }
        } else {
            toast.error(t('Please fill in all required fields.'));
        }
    };

    const handleDeclineConfirm = async () => {
        try {
            await axiosInstance.post('/admin/decline-request', {
                requestId: editableRequest.id,
                comment: declineComment
            });

            setShowDeclinePrompt(false);
            navigate('/registrationRequests');
            toast.success(t("Request Successfully Declined."));
        } catch (error) {
            console.error('Error declining request:', error);
            if (error?.response?.status === 403) {
                toast.error(t('Only SuperAdmin can decline requests.'));
            } else {
                toast.error(t('Error declining request. Please try again.'));
            }
        }
    };

    const handleApproveCancel = () => {
        setShowPrompt(false);
    };

    const handleDeclineCancel = () => {
        setShowDeclinePrompt(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEditableRequest((prevRequest) => {
            if (prevRequest[name] !== value) {
                return { ...prevRequest, [name]: value };
            }
            return prevRequest;
        });
    };

    const handleDownload = async (fileType) => {
        try {
            const response = await axiosInstance.get(`/user/trial-get-file/${editableRequest.emp_sap_id}/${fileType}`, {
                responseType: 'blob',
            });

            const contentDisposition = response.headers['content-disposition'];
            const fileNameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);
            const fileName = fileNameMatch ? fileNameMatch[1] : `${fileType}`;

            let fileExtension = 'bin';
            const mimeType = response.headers['content-type'];

            if (mimeType === 'application/pdf') {
                fileExtension = 'pdf';
            } else if (mimeType === 'image/jpeg') {
                fileExtension = 'jpg';
            } else if (mimeType === 'image/png') {
                fileExtension = 'png';
            }

            saveAs(new Blob([response.data], { type: mimeType }), `${fileName}.${fileExtension}`);
        } catch (error) {
            if (!error.response) {
                // Network error or no response
                toast.error(t("Looks like you are offline. Please check your internet connectivity."));
            } else {
                console.error('Error downloading file:', error);
                toast.error(t('Error downloading file. Please try again.'));
            }
        }
    };

    const handleFilePreview = async (fileType) => {
        setLoading(true); // Show spinner when fetching the file
        try {
            const response = await axiosInstance.get(`/user/trial-get-file/${editableRequest.emp_sap_id}/${fileType}`, {
                responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const url = URL.createObjectURL(blob);

            setPreviewUrl(url);
            setMimeType(response.headers['content-type']);
            setCurrentFileType(fileType);
            setShowPreviewModal(true); // Show the modal with the preview
            setLoading(false); // Hide spinner when the file is ready

        } catch (error) {
            if (!error.response) {
                // Network error or no response
                toast.error(t("Looks like you are offline. Please check your internet connectivity."));
            } else {
                console.error('Error fetching file:', error);
                toast.error(t('File not uploaded at the time of form submission.'));
                setLoading(false); // Hide spinner in case of an error
            }
        }
    };

    const handleCloseFilePreviewModal = () => {
        setShowPreviewModal(false);
        setPreviewUrl(null);
    };

    if (!editableRequest) return <p>Loading...</p>;

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getCurrentStepFields = () => {
        switch (step) {
            case 1:
                return ['emp_sap_id', 'full_name', 'admission_date', 'date_of_birth', 'place_of_birth', 'nationality', 'gender', 'marital_status'];
            case 2:
                return ['address', 'postal_code', 'locality', 'private_mobile', 'hf_cell_phone', 'private_email', 'hf_email', 'emergencyContactMobile', 'emergency_contact_type'];
            case 3:
                return ['cost_center', 'profession', 'hf_function', 'academic_qualifications', 'remarks']; //'company', 'branch', 'department', 'adminEmailId',
            case 4:
                return ['taxpayer_number', 'social_security_number', 'beneficiary_number', 'iban', 'swift', 'citizen_card_no_passport', 'expiry_date_cc_passport', 'driving_license_no', 'categories', 'driving_license_issue_date', 'driving_license_validity_date', 'no_of_dependents'];
            default:
                return [];
        }
    };

    const validateForm = (isSubmitting = false) => {
        let newErrors = {};
        const currentStepFields = getCurrentStepFields();

        currentStepFields.forEach(field => {
            if (!editableRequest[field]) {
                newErrors[field] = t('This field is required.');
            }
        });

        setErrors(newErrors);

        if (isSubmitting) {
            return Object.keys(newErrors).length === 0;
        } else {
            return Object.keys(newErrors).length === 0;
        }
    };

    return (
        <div className="request-details-container">
            <button className="backButton" onClick={handleBackClick}>
                <IoIosArrowBack style={{ marginRight: '5px' }} /> Back
            </button>
            <h2>{t("Request Details")}</h2>

            <div className="request-details-content">
                <div className='1'>
                    <h3>{t("Personal Information")}</h3>
                    <Row>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Employee SAP ID")}</Form.Label>
                                <Form.Control type="text" name="emp_sap_id" value={editableRequest.emp_sap_id} onChange={handleChange} />
                                {errors.emp_sap_id && <div className="error-message">{errors.emp_sap_id}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Full Name")}</Form.Label>
                                <Form.Control type="text" name="full_name" value={editableRequest.full_name} onChange={handleChange} />
                                {errors.full_name && <div className="error-message">{errors.full_name}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Admission Date")}</Form.Label>
                                <Form.Control type="date" name="admission_date" value={formatDate(editableRequest.admission_date)} onChange={handleChange} />
                                {errors.admission_date && <div className="error-message">{errors.admission_date}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Date of Birth")}</Form.Label>
                                <Form.Control type="date" name="date_of_birth" value={formatDate(editableRequest.date_of_birth)} onChange={handleChange} />
                                {errors.date_of_birth && <div className="error-message">{errors.date_of_birth}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Place of Birth")}</Form.Label>
                                <Form.Control type="text" name="place_of_birth" value={editableRequest.place_of_birth} onChange={handleChange} />
                                {errors.place_of_birth && <div className="error-message">{errors.place_of_birth}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Nationality")}</Form.Label>
                                <Form.Control type="text" name="nationality" value={editableRequest.nationality} onChange={handleChange} />
                                {errors.nationality && <div className="error-message">{errors.nationality}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Gender")}</Form.Label>
                                <Form.Control as="select" name="gender" value={editableRequest.gender} onChange={handleChange}>
                                    <option>{t("Select Gender")}</option>
                                    <option>{t("Male")}</option>
                                    <option>{t("Female")}</option>
                                    <option>{t("Other")}</option>
                                </Form.Control>
                                {errors.gender && <div className="error-message">{errors.gender}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Marital Status")}</Form.Label>
                                <Form.Control as="select" name="marital_status" value={editableRequest.marital_status} onChange={handleChange}>
                                    <option>{t("Select Martial Status")}</option>
                                    <option>{t("Single")}</option>
                                    <option>{t("Married")}</option>
                                    <option>{t("Divorced")}</option>
                                    <option>{t("Widowed")}</option>
                                </Form.Control>
                                {errors.marital_status && <div className="error-message">{errors.marital_status}</div>}
                            </Form.Group>
                        </Col>
                    </Row>
                </div>
                <div className='2'>
                    <h3>{t("Contact Information")}</h3>
                    <Row>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Address")}</Form.Label>
                                <Form.Control type="text" name="address" value={editableRequest.address} onChange={handleChange} />
                                {errors.address && <div className="error-message">{errors.address}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Postal Code")}</Form.Label>
                                <Form.Control type="text" name="postal_code" value={editableRequest.postal_code} onChange={handleChange} />
                                {errors.postal_code && <div className="error-message">{errors.postal_code}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Locality")}</Form.Label>
                                <Form.Control type="text" name="locality" value={editableRequest.locality} onChange={handleChange} />
                                {errors.locality && <div className="error-message">{errors.locality}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Private Mobile")}</Form.Label>
                                <Form.Control type="text" name="private_mobile" value={editableRequest.private_mobile} onChange={handleChange} />
                                {errors.private_mobile && <div className="error-message">{errors.private_mobile}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("HF Cell Phone")}</Form.Label>
                                <Form.Control type="text" name="hf_cell_phone" value={editableRequest.hf_cell_phone} onChange={handleChange} />
                                {errors.hf_cell_phone && <div className="error-message">{errors.hf_cell_phone}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Private Email")}</Form.Label>
                                <Form.Control type="email" name="private_email" value={editableRequest.private_email} onChange={handleChange} />
                                {errors.private_email && <div className="error-message">{errors.private_email}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("HF Email")}</Form.Label>
                                <Form.Control type="email" name="hf_email" value={editableRequest.hf_email} onChange={handleChange} />
                                {errors.hf_email && <div className="error-message">{errors.hf_email}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Emergency Contact (Mobile)")}</Form.Label>
                                <Form.Control type="text" name="emergency_contact_mobile" value={editableRequest.emergency_contact_mobile} onChange={handleChange} />
                                {errors.emergency_contact_mobile && <div className="error-message">{errors.emergency_contact_mobile}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Emergency Contact (Type)")}</Form.Label>
                                <Form.Control type="text" name="emergency_contact_type" value={editableRequest.emergency_contact_type} onChange={handleChange} />
                                {errors.emergency_contact_type && <div className="error-message">{errors.emergency_contact_type}</div>}
                            </Form.Group>
                        </Col>
                    </Row>
                </div>
                <div className='3'>
                    <h3>{t("Professional Information")}</h3>
                    <Row>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Company")}</Form.Label>
                                <Form.Control as="select" name='company_id' id="company_id" value={editableRequest.company_id || ''} onChange={handleCompanyChange}>
                                    <option value="">{t("Select Company")}</option>
                                    {companies.map(company => (
                                        <option key={company.company_id} value={company.company_id}>
                                            {company.company_name}
                                        </option>
                                    ))}
                                </Form.Control>
                                {/* {errors.company_id && <div className="error-message">{errors.company_id}</div>} */}
                            </Form.Group>
                        </Col><Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Branch")}</Form.Label>
                                <Form.Control as="select" name='branch_id' id="branch_id" value={editableRequest.branch_id || ''} onChange={handleBranchChange} disabled={!selectedCompany}>
                                    <option value="">{t("Select Branch")}</option>
                                    {branches.map(branch => (
                                        <option key={branch.branch_id} value={branch.branch_id}>
                                            {branch.branch_name}
                                        </option>
                                    ))}
                                </Form.Control>
                                {/* {errors.branch_id && <div className="error-message">{errors.branch_id}</div>} */}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Department")}</Form.Label>
                                <Form.Control as="select" name='dept_id' id="dept_id" value={editableRequest.dept_id || ''} onChange={handleDeptChange} disabled={!selectedBranch}>
                                    <option value="">{t("Select Department")}</option>
                                    {departments.map(dept => (
                                        <option key={dept.dept_id} value={dept.dept_id}>
                                            {dept.dept_name}
                                        </option>
                                    ))}
                                </Form.Control>
                                {/* {errors.dept_id && <div className="error-message">{errors.dept_id}</div>} */}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Manager Name")}</Form.Label>
                                <Form.Control as="select" name='admin_id' id="admin_id" value={editableRequest.admin_id || ''} onChange={handleManagerChange} disabled={!selectedDept}>
                                    <option value="">{t("Select Manager")}</option>
                                    {managerInfo.map(person => (
                                        <option key={person.emp_id} value={person.emp_id}>
                                            {person.p_name}
                                        </option>
                                    ))}
                                </Form.Control>
                                {/* {errors.adminEmailId && <div className="error-message">{errors.adminEmailId}</div>} */}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Assign Role")}</Form.Label>
                                <Form.Control as="select" name='role' id="role" value={empRole} onChange={(e) => setEmpRole(e.target.value)} >
                                    <option value="">{t("Select role")}</option>
                                    <option value={'User'}>{t("User")}</option>
                                    <option value={'Admin'}>{t("Admin")}</option>
                                </Form.Control>
                                {/* {errors.adminEmailId && <div className="error-message">{errors.adminEmailId}</div>} */}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Cost Center")}</Form.Label>
                                <Form.Control type="text" name="cost_center" value={editableRequest.cost_center} onChange={handleChange} />
                                {errors.cost_center && <div className="error-message">{errors.cost_center}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Profession")}</Form.Label>
                                <Form.Control type="text" name="profession" value={editableRequest.profession} onChange={handleChange} />
                                {errors.profession && <div className="error-message">{errors.profession}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("HF Function")}</Form.Label>
                                <Form.Control type="text" name="hf_function" value={editableRequest.hf_function} onChange={handleChange} />
                                {errors.hf_function && <div className="error-message">{errors.hf_function}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Academic Qualifications")}</Form.Label>
                                <Form.Control type="text" name="academic_qualifications" value={editableRequest.academic_qualifications} onChange={handleChange} />
                                {errors.academic_qualifications && <div className="error-message">{errors.academic_qualifications}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Remarks")}</Form.Label>
                                <Form.Control type="text" name="remarks" value={editableRequest.remarks} onChange={handleChange} />
                                {errors.remarks && <div className="error-message">{errors.remarks}</div>}
                            </Form.Group>
                        </Col>
                    </Row>
                </div>
                <div className='4'>
                    <h3>{t("Other Information")}</h3>
                    <Row>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Taxpayer Number")}</Form.Label>
                                <Form.Control type="text" name="taxpayer_number" value={editableRequest.taxpayer_number} onChange={handleChange} />
                                {errors.taxpayer_number && <div className="error-message">{errors.taxpayer_number}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Social Security Number")}</Form.Label>
                                <Form.Control type="text" name="social_security_number" value={editableRequest.social_security_number} onChange={handleChange} />
                                {errors.social_security_number && <div className="error-message">{errors.social_security_number}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Beneficiary Number")}</Form.Label>
                                <Form.Control type="text" name="beneficiary_number" value={editableRequest.beneficiary_number} onChange={handleChange} />
                                {errors.beneficiary_number && <div className="error-message">{errors.beneficiary_number}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>IBAN</Form.Label>
                                <Form.Control type="text" name="iban" value={editableRequest.iban} onChange={handleChange} />
                                {errors.iban && <div className="error-message">{errors.iban}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>SWIFT</Form.Label>
                                <Form.Control type="text" name="swift" value={editableRequest.swift} onChange={handleChange} />
                                {errors.swift && <div className="error-message">{errors.swift}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Citizen Card No. / Passport")}</Form.Label>
                                <Form.Control type="text" name="citizen_card_no_passport" value={editableRequest.citizen_card_no_passport} onChange={handleChange} />
                                {errors.citizen_card_no_passport && <div className="error-message">{errors.citizen_card_no_passport}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Expiry CC / Passport")}</Form.Label>
                                <Form.Control type="date" name="expiry_date_cc_passport" value={formatDateForMySQL(editableRequest.expiry_date_cc_passport)} onChange={handleChange} />
                                {errors.expiry_date_cc_passport && <div className="error-message">{errors.expiry_date_cc_passport}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Driving License No.")}</Form.Label>
                                <Form.Control type="text" name="driving_license_no" value={editableRequest.driving_license_no} onChange={handleChange} />
                                {errors.driving_license_no && <div className="error-message">{errors.driving_license_no}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Categories")}</Form.Label>
                                <Form.Control type="text" name="categories" value={editableRequest.categories} onChange={handleChange} />
                                {errors.categories && <div className="error-message">{errors.categories}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Driving License Issue")}</Form.Label>
                                <Form.Control type="date" name="driving_license_issue_date" value={formatDate(editableRequest.driving_license_issue_date)} onChange={handleChange} />
                                {errors.driving_license_issue_date && <div className="error-message">{errors.driving_license_issue_date}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("Driving License Validity")}</Form.Label>
                                <Form.Control type="date" name="driving_license_validity_date" value={formatDate(editableRequest.driving_license_validity_date)} onChange={handleChange} />
                                {errors.driving_license_validity_date && <div className="error-message">{errors.driving_license_validity_date}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6} className='mb-2'>
                            <Form.Group>
                                <Form.Label>{t("No. of Dependents")}</Form.Label>
                                <Form.Control type="number" name="no_of_dependents" value={editableRequest.no_of_dependents} onChange={handleChange} />
                                {errors.no_of_dependents && <div className="error-message">{errors.no_of_dependents}</div>}
                            </Form.Group>
                        </Col>
                        {/* <Col md={6} className='mb-2'>
                            <PasswordInput password={editableRequest.password}
                                confirmPassword={editableRequest.confirmPassword}
                                onPasswordChange={handlePasswordChange} />
                        </Col> */}
                    </Row>
                </div>
                <div>
                    <div className="file-downloads">
                        <h6>{t("Download Documents")}:</h6>
                        <button className='download-btn' onClick={() => handleFilePreview('citizenCard')}>{t("Citizen Card")}</button>
                        <button className='download-btn' onClick={() => handleFilePreview('drivingLicense')}>{t("Driving License")}</button>
                        <button className='download-btn' onClick={() => handleFilePreview('cv')}>{t("Preview CV")}</button>
                        <button className='download-btn' onClick={() => handleFilePreview('irsForm')}>{t("IRS Form")}</button>
                        <button className='download-btn' onClick={() => handleFilePreview('academicQualifications')}>{t("Academic Qualifications")}</button>
                    </div>

                    <Modal show={showPreviewModal} onHide={handleCloseFilePreviewModal} size="lg" centered>
                        <Modal.Header closeButton>
                            <Modal.Title>{("File Preview")}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {loading ? (
                                <div className="text-center">
                                    <Spinner animation="border" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </Spinner>
                                </div>
                            ) : previewUrl && mimeType.startsWith('image/') ? (
                                <img
                                    src={previewUrl}
                                    alt="File Preview"
                                    style={{
                                        width: '100%',  // Full width within the modal
                                        height: 'auto', // Maintain aspect ratio
                                        objectFit: 'contain',
                                    }}
                                />
                            ) : (
                                <iframe
                                    src={previewUrl}
                                    title="File Preview"
                                    width="100%"
                                    height="500px"
                                    style={{ border: 'none' }}
                                />
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseFilePreviewModal}>{t("Close")}</Button>
                            <Button variant="primary" onClick={() => handleDownload(currentFileType)}>{t("Download")}</Button>
                        </Modal.Footer>
                    </Modal>
                </div>
                {loggedInEmpRole === 'SuperAdmin' && (
                    <div className='handle-button-click'>
                        <button onClick={handleApproveClick} className="btn btn-success">{t("Approve")}</button>
                        <button onClick={handleDeclineClick} className="btn btn-danger">{t("Decline")}</button>
                    </div>
                )}
            </div>

            {showPrompt && (
                <div className="approval-prompt">
                    <div className='prompt-content'>
                        <p>{t("Are you sure you want to approve this request?")}</p>
                        <div className="prompt-buttons">
                            <Button variant="success" onClick={handleApproveConfirm} disabled={isSubmitting}>{t("Confirm")}</Button>
                            <Button variant="danger" onClick={handleApproveCancel}>{t("Cancel")}</Button>
                        </div>
                    </div>
                </div>
            )}

            {showDeclinePrompt && (
                <div className="approval-prompt">
                    <div className="prompt-content">
                        <p>{t("Please provide a comment for declining this request:")}</p>
                        <input
                            type="text"
                            value={declineComment}
                            onChange={(e) => setDeclineComment(e.target.value)}
                            placeholder="Comment"
                        />
                        <div className="prompt-buttons">
                            <button onClick={handleDeclineConfirm} className="confirm-button">{t("Submit")}</button>
                            <button onClick={handleDeclineCancel} className="cancel-button">{t("Cancel")}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RequestDetails;
